/**
 * MusicXMLPreview - Renders MusicXML using OpenSheetMusicDisplay
 */
/// <reference lib="dom" />
import { useEffect, useRef, useState } from "hono/jsx";

interface MusicXMLPreviewProps {
  musicxml: string;
  width?: number; // Optional width override for auto-scale
}

export const MusicXMLPreview = ({
  musicxml,
  width,
}: MusicXMLPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderTargetRef = useRef<HTMLDivElement>(null);

  // Auto-scale logic:
  // If we have a custom width, calculate scale to fit the 450px container
  // Max scale limited to 2.5 (large but not huge), Min scale effectively defined by width 210 -> ~2.0
  const maxContainerWidth = 450;
  const contentWidth = width || 210;
  const targetScale = Math.min(2.5, maxContainerWidth / contentWidth);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Prevent rendering if not client-side or no ref
    if (typeof window === "undefined" || !renderTargetRef.current) return;

    // Reset previous content
    if (renderTargetRef.current) {
      renderTargetRef.current.innerHTML = "";
    }

    if (!musicxml) return;

    let osmd: any | null = null; // Typing workaround for dynamic import
    let isActive = true;

    const renderScore = async () => {
      try {
        setError(null);

        const { OpenSheetMusicDisplay } = await import(
          "opensheetmusicdisplay"
        );

        if (!isActive) return;

        if (!renderTargetRef.current) return;

        // Initialize OSMD
        osmd = new OpenSheetMusicDisplay(renderTargetRef.current, {
          autoResize: false, // We handle scaling manually via CSS transform
          backend: "svg",
          drawingParameters: "preview", // Compact mode
          drawTitle: false,
          drawSubtitle: false,
          drawComposer: false,
          drawLyricist: false,
          drawPartNames: false,
          drawMetronomeMarks: false,
          drawTimeSignatures: false, // Only show clef/key/notes
          drawMeasureNumbers: false,
        });

        await osmd.load(musicxml);

        if (!isActive) return;

        osmd.render();
      } catch (err) {
        console.error("OSMD Render Error:", err);
        setError("Invalid MusicXML");
      }
    };

    // Wait slightly for container to be ready, then render
    // Use a small timeout or requestAnimationFrame to ensure DOM is ready
    const timer = setTimeout(() => {
      renderScore();
    }, 50);

    return () => {
      isActive = false;
      clearTimeout(timer);
      if (osmd) {
        // cleanup if needed (OSMD doesn't have strict destroy)
      }
    };
  }, [musicxml, width]); // Re-render when xml or width changes

  return (
    <div class="musicxml-preview w-full flex justify-center py-4">
      {error && (
        <div class="text-error text-sm mb-2 font-bold absolute">
          Error: {error}
        </div>
      )}
      <div
        ref={containerRef}
        class="bg-white text-black rounded-lg shadow-sm border border-gray-200 overflow-hidden relative"
        style={{
          height: "250px",
          width: "450px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          ref={renderTargetRef}
          style={{
            width: `${width}px`,
            transform: `scale(${targetScale})`,
            transformOrigin: "center center",
            display: "flex",
            justifyContent: "center",
          }}
        />
      </div>
    </div>
  );
};
